import { TagInput } from '@/components/custom/TagInput';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAffiliatesApi } from '@/hooks/useAffiliatesApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Plus,
  Search,
  Tag,
  Trash2,
  UserCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  invited: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  expired: 'bg-red-500/10 text-red-500 border-red-500/20',
};

interface MemberData {
  id: string;
  name: string;
  email: string;
  status: string;
  joined_at: string;
  role?: string;
}

export default function AffiliateMembers() {
  const affiliatesApi = useAffiliatesApi();
  const queryClient = useQueryClient();
  // FEAT-APR30-001 — owner Dashboard's "Active Members" KPI card deep-links
  // here with ?filter=active. Read it once on mount; user can clear via the
  // status filter UI below.
  const [searchParams, setSearchParams] = useSearchParams();
  const filterFromUrl = searchParams.get('filter') || '';
  const [statusFilter, setStatusFilter] = useState<string>(
    filterFromUrl === 'active' ? 'active' : ''
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [csvError, setCsvError] = useState<string | null>(null);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<MemberData | null>(null);
  const limit = 10;

  const { data, isLoading, error } = useQuery({
    queryKey: ['affiliate-members', page, searchQuery],
    queryFn: async () => {
      const response = await affiliatesApi.getMembers({
        page,
        limit,
        search: searchQuery || undefined,
      });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  // Fetch affiliate tag definitions for autocomplete
  const { data: tagsData } = useQuery({
    queryKey: ['affiliate-tags'],
    queryFn: async () => {
      const response = await affiliatesApi.getTags();
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  // Fetch tags for expanded member
  const { data: memberTagsData } = useQuery({
    queryKey: ['member-tags', expandedMember],
    queryFn: async () => {
      if (!expandedMember) return { tags: [] };
      const response = await affiliatesApi.getMemberTags(expandedMember);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!expandedMember,
  });

  const inviteMutation = useMutation({
    mutationFn: async ({ email, name }: { email: string; name?: string }) => {
      const response = await affiliatesApi.inviteMember(email, name);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Invitation sent!');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      queryClient.invalidateQueries({ queryKey: ['affiliate-members'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to send invitation');
    },
  });

  const bulkInviteMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      const response = await affiliatesApi.bulkInvite(emails);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (data) => {
      const invited = data?.summary?.success ?? data?.invited ?? 0;
      const failed = data?.summary?.failed ?? data?.failed ?? 0;
      toast.success(`Sent ${invited} invitations`);
      if (failed > 0) {
        toast.warning(`${failed} invitations failed`);
      }
      setShowBulkInviteModal(false);
      setBulkEmails('');
      queryClient.invalidateQueries({ queryKey: ['affiliate-members'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to send invitations');
    },
  });

  const changeRoleMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: string }) => {
      const response = await affiliatesApi.changeMemberRole(memberId, role);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || 'Role updated');
      queryClient.invalidateQueries({ queryKey: ['affiliate-members'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to change role');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const response = await affiliatesApi.removeMember(memberId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Member removed');
      setConfirmRemove(null);
      queryClient.invalidateQueries({ queryKey: ['affiliate-members'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to remove member');
    },
  });

  const addTagMutation = useMutation({
    mutationFn: async ({ memberId, tag }: { memberId: string; tag: string }) => {
      const response = await affiliatesApi.addMemberTag(memberId, tag);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-tags', expandedMember] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-tags'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to add tag');
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: async ({ memberId, tagId }: { memberId: string; tagId: string }) => {
      const response = await affiliatesApi.removeMemberTag(memberId, tagId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-tags', expandedMember] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-tags'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to remove tag');
    },
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (inviteEmail) {
      inviteMutation.mutate({ email: inviteEmail, name: inviteName || undefined });
    }
  };

  const handleBulkInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const emails = bulkEmails
      .split(/[\n,;]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0 && e.includes('@'));
    if (emails.length > 0) {
      bulkInviteMutation.mutate(emails);
    } else {
      toast.error('Please enter valid email addresses');
    }
  };

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      setCsvError('Please upload a .csv file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (!lines.length) {
        setCsvError('CSV file is empty');
        return;
      }
      // Detect header row and find email column index
      const headers = lines[0]?.split(',').map((h) => h.trim().toLowerCase()) ?? [];
      const emailColIdx = headers.indexOf('email');
      const startRow = emailColIdx >= 0 ? 1 : 0;
      const emails: string[] = [];
      for (let i = startRow; i < lines.length; i++) {
        const cols = lines[i]?.split(',') ?? [];
        const email = emailColIdx >= 0 ? cols[emailColIdx] : cols[0];
        const trimmed = email?.trim().replace(/^"|"$/g, '');
        if (trimmed?.includes('@')) emails.push(trimmed);
      }
      if (!emails.length) {
        setCsvError('No valid emails found in CSV');
        return;
      }
      setBulkEmails((prev) => {
        const existing = prev.trim();
        return existing ? `${existing}\n${emails.join('\n')}` : emails.join('\n');
      });
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Pending';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalPages = Math.ceil((data?.pagination?.total || 0) / limit);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <p className="text-destructive">Failed to load members</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // FEAT-APR30-001 — apply ?filter=active client-side so the Active Members
  // KPI deep-link shows only active members without a backend roundtrip.
  const allMembers: MemberData[] = data?.members || [];
  const visibleMembers: MemberData[] =
    statusFilter === 'active' ? allMembers.filter((m) => m.status === 'active') : allMembers;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Member Roster</h1>
          <p className="mt-1 text-muted-foreground">Manage your gym members, roles, and tags.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBulkInviteModal(true)}>
            <Mail className="h-4 w-4 mr-2" />
            Bulk Invite
          </Button>
          <Button onClick={() => setShowInviteModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="py-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search members by name or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
          {statusFilter === 'active' && (
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className={statusColors.active}>
                Active members only
              </Badge>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('');
                  searchParams.delete('filter');
                  setSearchParams(searchParams, { replace: true });
                }}
              >
                Show all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Join Requests (owner only) */}
      <PendingJoinRequests affiliatesApi={affiliatesApi} queryClient={queryClient} />

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Members (
            {statusFilter === 'active'
              ? `${visibleMembers.length} of ${data?.pagination?.total || 0}`
              : data?.pagination?.total || 0}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          {visibleMembers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <UserPlus className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No members yet</h3>
              <p className="text-muted-foreground max-w-md mb-4">
                {searchQuery
                  ? 'No members match your search.'
                  : 'Invite members to join your gym and start logging scores.'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setShowInviteModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Your First Member
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="w-10">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleMembers.map((member: MemberData, index: number) => (
                    <>
                      <TableRow
                        key={member.id}
                        className="animate-fade-in"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <TableCell className="font-medium">{member.name}</TableCell>
                        <TableCell className="text-muted-foreground">{member.email}</TableCell>
                        <TableCell>
                          <Select
                            value={member.role || 'member'}
                            onChange={(e) =>
                              changeRoleMutation.mutate({
                                memberId: member.id,
                                role: e.target.value,
                              })
                            }
                            options={[
                              { value: 'member', label: 'Member' },
                              { value: 'coach', label: 'Coach' },
                            ]}
                            className="h-7 w-24 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusColors[member.status] || ''}>
                            {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(member.joined_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title="Manage Tags"
                              onClick={() =>
                                setExpandedMember(expandedMember === member.id ? null : member.id)
                              }
                            >
                              <Tag className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              title="Remove Member"
                              onClick={() => setConfirmRemove(member)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded tag row */}
                      {expandedMember === member.id && (
                        <TableRow key={`${member.id}-tags`}>
                          <TableCell colSpan={6} className="bg-muted/30">
                            <div className="py-2 px-2">
                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                Tags for {member.name}
                              </p>
                              <TagInput
                                tags={memberTagsData?.tags || []}
                                suggestions={tagsData?.tags || []}
                                onAdd={(tag) => addTagMutation.mutate({ memberId: member.id, tag })}
                                onRemove={(tagId) =>
                                  removeTagMutation.mutate({ memberId: member.id, tagId })
                                }
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-border pt-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to{' '}
                    {Math.min(page * limit, data?.pagination?.total || 0)} of{' '}
                    {data?.pagination?.total || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Remove Confirmation Dialog */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-sm mx-4 animate-fade-in">
            <CardHeader>
              <CardTitle className="text-destructive">Remove Member</CardTitle>
              <CardDescription>
                Are you sure you want to remove <strong>{confirmRemove.name}</strong> from your gym?
                They will lose access to workouts and leaderboards.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => removeMemberMutation.mutate(confirmRemove.id)}
                  disabled={removeMemberMutation.isPending}
                >
                  {removeMemberMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Remove
                </Button>
                <Button variant="outline" onClick={() => setConfirmRemove(null)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Invite Member</CardTitle>
                  <CardDescription>Send an invitation to join your gym.</CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowInviteModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email *</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="member@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Name (optional)</Label>
                  <Input
                    id="invite-name"
                    type="text"
                    placeholder="John Doe"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={inviteMutation.isPending}>
                    {inviteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Send Invitation
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Invite Modal */}
      {showBulkInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle>Bulk Invite</CardTitle>
                  <CardDescription>
                    Paste multiple email addresses (one per line or comma-separated).
                  </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowBulkInviteModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBulkInvite} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="csv-upload">Upload CSV (email, name columns)</Label>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleCsvUpload}
                    className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                  />
                  {csvError && <p className="text-xs text-destructive">{csvError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-emails">Email Addresses</Label>
                  <textarea
                    id="bulk-emails"
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="john@example.com&#10;jane@example.com&#10;bob@example.com"
                    value={bulkEmails}
                    onChange={(e) => setBulkEmails(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    One per line, or paste from CSV upload above
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1" disabled={bulkInviteMutation.isPending}>
                    {bulkInviteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Send Invitations
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBulkInviteModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}

/**
 * Pending Join Requests component — shown to gym owners
 * Displays requests from users who found the gym via the directory
 */
function PendingJoinRequests({
  affiliatesApi,
  queryClient,
}: {
  affiliatesApi: ReturnType<typeof useAffiliatesApi>;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['join-requests'],
    queryFn: async () => {
      const res = await affiliatesApi.getJoinRequests();
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    refetchInterval: 30000,
  });

  const approveMutation = useMutation({
    mutationFn: (requestId: string) => affiliatesApi.handleJoinRequest(requestId, 'approve'),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Member approved');
      queryClient.invalidateQueries({ queryKey: ['join-requests'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-members'] });
    },
    onError: () => toast.error('Failed to approve request'),
  });

  const rejectMutation = useMutation({
    mutationFn: (requestId: string) => affiliatesApi.handleJoinRequest(requestId, 'reject'),
    onSuccess: (res) => {
      toast.success(res.data?.message || 'Request declined');
      queryClient.invalidateQueries({ queryKey: ['join-requests'] });
    },
    onError: () => toast.error('Failed to decline request'),
  });

  const pendingRequests = data?.requests?.filter((r) => r.status === 'pending') || [];

  // Don't render anything if no pending requests and not loading
  if (!isLoading && pendingRequests.length === 0) return null;

  return (
    <Card className="mb-6 border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCheck className="h-5 w-5 text-primary" />
          Pending Join Requests
          {pendingRequests.length > 0 && (
            <Badge variant="default" className="ml-1">
              {pendingRequests.length}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>Membership and role requests for your gym</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            {pendingRequests.map((req) => (
              <div
                key={req.id}
                className="flex items-center justify-between p-3 rounded-lg border border-border bg-card"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{req.name}</p>
                    {req.requested_role && req.requested_role !== 'member' && (
                      <Badge variant="outline" className="text-xs capitalize">
                        {req.requested_role}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{req.email}</p>
                  {req.assigned_by_name && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Assigned by <span className="font-medium">{req.assigned_by_name}</span>
                    </p>
                  )}
                  {req.message && !req.assigned_by_name && (
                    <p className="text-xs text-muted-foreground mt-1 italic">
                      &ldquo;{req.message}&rdquo;
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(req.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex gap-2 ml-3">
                  <Button
                    size="sm"
                    onClick={() => approveMutation.mutate(req.id)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Check className="h-3 w-3 mr-1" />
                    )}
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rejectMutation.mutate(req.id)}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
